import type { SyncPushOperation } from "../types";
import {
  applyAcceptedRecord,
  applyRemoteRecord,
  db,
  getMeta,
  preserveLocalAndApplyConflict,
  setMeta
} from "./db";
import { ApiError, pullChanges, pushChanges } from "./api";

let activeSync: Promise<SyncResult> | null = null;

export interface SyncResult {
  pushed: number;
  pulled: number;
  conflicts: number;
}

async function runSync(): Promise<SyncResult> {
  const pending = await db.outbox.orderBy("createdAt").toArray();
  const sent = new Map(pending.map((item) => [item.recordId, item]));
  let conflicts = 0;
  let pushed = 0;

  if (pending.length > 0) {
    for (let offset = 0; offset < pending.length; offset += 100) {
      const batch = pending.slice(offset, offset + 100).map<SyncPushOperation>((item) => ({
        opId: item.opId,
        baseRevision: item.baseRevision,
        record: item.record
      }));
      const response = await pushChanges(batch);
      for (const result of response.results) {
        const original = sent.get(result.record.id);
        if (!original) continue;
        if (result.status === "accepted") {
          await applyAcceptedRecord(result.record, original.record.updatedAt);
          pushed += 1;
        } else {
          await preserveLocalAndApplyConflict(result.record);
          conflicts += 1;
        }
      }
    }
  }

  let cursor = Number((await getMeta("syncCursor")) ?? 0);
  let pulled = 0;
  for (let page = 0; page < 30; page += 1) {
    const response = await pullChanges(cursor);
    for (const change of response.changes) {
      await applyRemoteRecord(change.record);
      pulled += 1;
    }
    cursor = response.nextCursor;
    await setMeta("syncCursor", String(cursor));
    if (response.changes.length < 500) break;
  }
  await setMeta("lastSyncedAt", new Date().toISOString());
  return { pushed, pulled, conflicts };
}

export function syncNow(): Promise<SyncResult> {
  if (activeSync) return activeSync;
  activeSync = runSync().finally(() => {
    activeSync = null;
  });
  return activeSync;
}

export function isAuthError(error: unknown): boolean {
  return error instanceof ApiError && error.status === 401;
}

export function isNetworkError(error: unknown): boolean {
  return error instanceof ApiError && error.code === "NETWORK_ERROR";
}
